const {
  toBackendDate, toPlannedDate, resolvePointDate,
} = require('./documentImportDates');

function buildCountryAndCity(place) {
  const components = (place && place.address_components) || [];
  const countryComp = components.find(c => c.types.indexOf('country') !== -1);
  const cityComp = components.find(c => c.types.indexOf('locality') !== -1);
  const country = countryComp ? countryComp.long_name : '';
  const city = cityComp ? cityComp.long_name : '';
  if (country && city) return country + ', ' + city;
  return country || city || '';
}

function pointTitle(point, t) {
  if (point.labelKey) {
    const word = t(point.labelKey);
    return point.labelSuffix ? `${word} · ${point.labelSuffix}` : word;
  }
  return point.label || point.place.name;
}

function isActive(result) {
  return result && result.status === 'done' && !result.discarded;
}

/**
 * The writes needed to put these parsed documents onto a ride, in the order the
 * ride expects them: stays, then places, then day notes, then the trip-level
 * sections. No HTTP here — the executor performs them.
 *
 * Every operation carries `resultIndex`: the position in `results` it was built
 * from, so the executor can report which documents landed on the ride in full
 * and a retry after a partial failure cannot create them a second time. The
 * trip-level sections belong to no single document and carry null.
 */
function buildAttachOperations({ results, generalInfo, rideStartDate, t, images }) {
  const ops = [];
  const all = results || [];
  const pics = images || {};

  all.forEach((r, resultIndex) => {
    if (!(isActive(r) && r.type === 'accommodation' && r.place !== null)) return;
    const d = r.extracted.data;
    ops.push({
      kind: 'accommodation',
      resultIndex,
      sourceName: d.name || '',
      payload: {
        name: d.name || '',
        address: d.address || '',
        text: d.text || '',
        link: d.link || '',
        bookingConfirmation: d.booking_confirmation || '',
        checkIn: toBackendDate(d.check_in_date),
        checkOut: toBackendDate(d.check_out_date),
        lat: r.place.geometry.location.lat(),
        lng: r.place.geometry.location.lng(),
        contryAndCity: buildCountryAndCity(r.place),
        place_id: r.place.place_id,
        voucherUrls: r.voucherUrl ? [r.voucherUrl] : [],
        rate: null,
        image: pics.accommodation || '',
      },
    });
  });

  all.forEach((r, resultIndex) => {
    if (!(isActive(r) && r.type !== 'accommodation')) return;
    (r.points || []).forEach((point) => {
      if (!point.place) return;
      ops.push({
        kind: 'attraction',
        resultIndex,
        sourceName: point.label || '',
        payload: {
          title: pointTitle(point, t),
          text: point.place.name
            ? (point.place.name + (point.text ? '\n' + point.text : ''))
            : (point.text || ''),
          lat: point.place.geometry.location.lat(),
          lng: point.place.geometry.location.lng(),
          address: point.place.formatted_address || '',
          placeId: point.place.place_id,
          // A document that states no dates leaves every point without one.
          // Those places go on the ride's first day rather than being rejected
          // by the server and silently left off the ride.
          plannedDate: toPlannedDate(resolvePointDate(point, rideStartDate), point.time),
          voucherUrls: (r.type !== 'itinerary' && r.voucherUrl) ? [r.voucherUrl] : [],
          link: '',
          rate: null,
          categoryId: null,
          subcategoryId: null,
          videoUrl: '',
          audioUrl: '',
          imageUrl: pics[point.labelKey] || pics[r.type] || '',
          // With no image above, the server uses the place's admin-approved one, if any.
          fillSharedImage: true,
        },
      });
    });
  });

  all.forEach((r, resultIndex) => {
    if (!(isActive(r) && r.type === 'itinerary')) return;
    ((r.extracted.data || {}).day_notes || []).forEach((note) => {
      if (!note.title && !note.note) return;
      // Only notes the document actually tied to a day; the undated ones became
      // general information when the document was read.
      if (!note.date && !note.day_number) return;
      const noteDate = resolvePointDate(
        { date: note.date, dayNumber: note.day_number }, rideStartDate,
      );
      if (!noteDate) return;
      ops.push({
        kind: 'extraInfo',
        resultIndex,
        sourceName: note.title || '',
        payload: { title: note.title || '', text: note.note || '', date: noteDate },
      });
    });
  });

  // Trip-level sections — insurance, currency, tipping and the like. Posted
  // without a date on purpose: an extra-information row with no day association
  // is what the ride renders as its general information block.
  //
  // `sectionIndex` is its position in the generalInfo array it came from. These
  // belong to no card, so if one is rejected nothing else remembers it existed;
  // the index is what lets the store keep exactly the sections that did not
  // land, and drop exactly the ones that did.
  (generalInfo || []).forEach((section, sectionIndex) => {
    ops.push({
      kind: 'extraInfo',
      resultIndex: null,
      sectionIndex,
      sourceName: section.title || '',
      payload: { title: section.title, text: section.text, date: '' },
    });
  });

  return ops;
}

module.exports = { buildAttachOperations, buildCountryAndCity, pointTitle };
